// npm reports @capgo/inappbrowser as moved to @capgo/capacitor-inappbrowser.
// The rename cannot be taken on its own: every published version of the new name
// requires @capacitor/core >= 8 and this app is on 7. 7.29.4 is the newest
// release of the same codebase that supports Capacitor 7 — pinned, not stale.
//
// Take it with the Capacitor 8 upgrade, which is a costed ticket rather than a
// wall: every Capacitor-family package here has an 8.x release. core/android/ios
// move together to 8.5.x, core must be >= 8.0.2 (camera-preview's peer), and the
// Cordova plugins are unaffected.
import { InAppBrowser, ToolBarType } from "@capgo/inappbrowser";
import axios from "axios";
import * as Sentry from "@sentry/react";
import type { PluginListenerHandle } from "@capacitor/core";
import { AssignmentPlatform } from "refsix-core";
import type { ScrapePayload } from "refsix-core";
import request from "../request";
import { installMockInAppBrowser } from "./mockInAppBrowser";
import {
    isPlatformUrl,
    parseScrapeMessage,
    scrapeDisposition,
    shouldRedirectToSchedule,
    type ScrapeDisposition,
} from "./inAppScrapeMessages";

/**
 * Importing appointments in the app, without the browser extension (REFSIX-568).
 *
 * The extension exists because Arbiter Sports and MOAS have no public API, so
 * the only place a referee's schedule can be read is a browser holding their
 * session. An in-app WebView is such a browser: it has the same three
 * primitives an extension gets — a page with the user's cookies, a way to run
 * our script in it, and a channel back — so the same scrape works on the phone,
 * which is where referees actually are.
 *
 * The flow:
 *
 *   1. open the platform's schedule page in the WebView, and let the user sign
 *      in there, on the platform's own login form;
 *   2. inject the scraper on every page load. Until the user reaches their
 *      schedule it reports "not a schedule page" and we keep waiting;
 *   3. when it reads a schedule, close the WebView and POST the fixtures to
 *      refauth's existing `/integrations/assignments/matches` — the same
 *      endpoint, payload and validation the extension uses;
 *   4. report what refauth did with them.
 *
 * The user's platform credentials never reach app code, let alone refauth: they
 * are typed into the platform's page inside the WebView, and only parsed
 * fixtures cross back.
 *
 * Session persistence is the WebView's own cookie jar, so a second import
 * usually needs no sign-in. It is not shared with Safari or Chrome, so the first
 * one always does.
 */

/**
 * Where the built scraper lands — see scripts/build-assignments-scraper.js.
 *
 * Absolute, as `/r6watch/index.html` is: this page's own URL is a deep route
 * ("…/more/integrations/moas"), so a relative path would resolve against that
 * and 404.
 */
const SCRAPER_URL = "/injected/assignments-scraper.js";

const IMPORT_PATH = "/integrations/assignments/matches";

/**
 * The plugin, or the local stand-in in a build that mocks it.
 *
 * Resolved per call, not captured at module load: the mock is installed during
 * app init, which can be after this module is first imported. Installing here
 * too (it is idempotent) means the stand-in is in place however app init is
 * ordered.
 *
 * The seam is here rather than at Capacitor because `registerPlugin` overwrites
 * `Capacitor.Plugins.InAppBrowser` with its own proxy — unlike the UXCam mock,
 * there is nothing to substitute there. See `mockInAppBrowser.ts`.
 */
type InAppBrowserApi = Pick<
    typeof InAppBrowser,
    "addListener" | "openWebView" | "executeScript" | "close" | "setUrl"
>;

function browser(): InAppBrowserApi {
    return (
        (installMockInAppBrowser() as unknown as InAppBrowserApi) ??
        InAppBrowser
    );
}

/**
 * How long to leave the WebView open with nothing happening.
 *
 * Generous because the user is signing in inside it, possibly fetching a
 * password from elsewhere on the phone — which backgrounds the app. It is a
 * backstop against a leaked listener, not a UX timer: the user can close the
 * WebView whenever they like, and that resolves the import as cancelled.
 */
const OVERALL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * The trail of an import, attached to whatever is reported afterwards. An import
 * is a conversation with a site we do not control, so when one goes wrong the
 * only useful evidence is which of its pages the user reached.
 */
function breadcrumb(message: string): void {
    Sentry.addBreadcrumb({ category: "assignments", message });
}

export interface ImportCounts {
    added: number;
    updated: number;
    unchanged: number;
    skipped: number;
}

export type InAppImportOutcome =
    | ({ status: "imported"; platform: string } & ImportCounts)
    /** The user closed the WebView before a schedule was read. */
    | { status: "cancelled" }
    /** A schedule page we could not turn into an import. */
    | {
          status: "notImported";
          reason: "noFixtures" | "allInPast" | "tooMany" | "error";
          found?: number;
          diagnostics?: string;
      }
    /** The scrape worked; sending it to REFSIX did not. */
    | { status: "importFailed"; sessionExpired?: boolean }
    /** Nothing happened for OVERALL_TIMEOUT_MS. */
    | { status: "timedOut" };

/**
 * refauth answers with the documents it touched. The app only shows counts, so
 * the arrays are reduced here rather than carried around.
 */
interface ImportResponse {
    added?: unknown[];
    updated?: unknown[];
    unchanged?: unknown[];
    invalid?: unknown[];
}

export function summariseImportResponse(
    body: ImportResponse | undefined
): ImportCounts {
    const size = (value: unknown[] | undefined) =>
        Array.isArray(value) ? value.length : 0;

    return {
        added: size(body?.added),
        updated: size(body?.updated),
        unchanged: size(body?.unchanged),
        // refauth calls these "invalid": rows it could not turn into a match.
        // "Skipped" is what the user needs to hear — the import as a whole
        // succeeded, and these rows were left out of it.
        skipped: size(body?.invalid),
    };
}

/**
 * The scraper as text, ready for `executeScript`.
 *
 * Fetched from the app's own origin rather than bundled as a string constant, so
 * a 200KB scrape bundle does not ride along in the app bundle for every user who
 * never opens this page.
 */
async function scraperCode(): Promise<string> {
    const response = await fetch(SCRAPER_URL);
    if (!response.ok) {
        // Generated at build time, so this is a broken build rather than
        // anything the user did — and it is worth saying so plainly, because the
        // symptom otherwise is a WebView that never finds a schedule.
        throw new Error(
            `The assignment scraper is missing (${response.status}). ` +
                "Run `npm run assignments-scraper` in phone/."
        );
    }
    return response.text();
}

export async function importAssignmentsInApp(
    platform: AssignmentPlatform
): Promise<InAppImportOutcome> {
    const code = await scraperCode();

    const read = await readScheduleInWebView(platform, code);
    if (read.status !== "read") {
        return read.outcome;
    }

    return sendToRefsix(read.payload);
}

type ScheduleRead =
    | { status: "read"; payload: ScrapePayload }
    | { status: "gaveUp"; outcome: InAppImportOutcome };

/**
 * Opens the WebView and resolves once the scraper has read a schedule out of it,
 * or the attempt ended some other way.
 *
 * Deliberately one function: the listeners, the timeout and the WebView are a
 * single lifetime, and splitting them up is how you leak one of them.
 */
async function readScheduleInWebView(
    platform: AssignmentPlatform,
    code: string
): Promise<ScheduleRead> {
    const listeners: PluginListenerHandle[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    // At most one automatic navigation per import — see shouldRedirectToSchedule.
    let sentToSchedule = false;

    return new Promise<ScheduleRead>((resolve) => {
        const finish = (read: ScheduleRead, closeWebView: boolean) => {
            if (settled) {
                return;
            }
            settled = true;

            if (timer) {
                clearTimeout(timer);
            }
            listeners.forEach((listener) => {
                listener.remove().catch((err) =>
                    console.warn(
                        "Could not remove an in-app browser listener",
                        err
                    )
                );
            });

            if (closeWebView) {
                browser().close().catch((err) =>
                    console.warn("Could not close the in-app browser", err)
                );
            }

            resolve(read);
        };

        const act = (disposition: ScrapeDisposition, url: string) => {
            switch (disposition.act) {
                case "waiting":
                    // Every page on the way to the schedule lands here, the
                    // login page included. A breadcrumb rather than a warning
                    // because it is the normal case — but when an import does
                    // stall it is the only record of what the user was looking
                    // at when it did.
                    breadcrumb(
                        `not a ${platform.id} schedule page yet: ${url} ${
                            disposition.diagnostics ?? ""
                        }`
                    );

                    // Signed in, but on the platform's own pages rather than the
                    // schedule — Arbiter returns you to a dashboard that has no
                    // fixture table. Go and fetch the page we actually need.
                    if (
                        !sentToSchedule &&
                        shouldRedirectToSchedule(url, platform.scheduleUrl)
                    ) {
                        sentToSchedule = true;
                        breadcrumb(
                            `sending the WebView to the ${platform.id} schedule page`
                        );
                        browser()
                            .setUrl({ url: platform.scheduleUrl })
                            .catch((err) =>
                                console.warn(
                                    "Could not navigate the in-app browser to the schedule page",
                                    err
                                )
                            );
                    }
                    return;

                case "import": {
                    const payload = disposition.result.payload;

                    // The scrape names its own platform, from whichever adapter
                    // claimed the page. It should always be the one whose page we
                    // opened — but the import is keyed on this, and importing one
                    // platform's fixtures under another's id prefix would make
                    // duplicates nobody can merge.
                    if (payload.platform !== platform.id) {
                        console.warn(
                            `REFSIX: a ${payload.platform} scrape arrived on the ${platform.id} page`
                        );
                        return;
                    }

                    breadcrumb(
                        `read ${payload.fixtures.length} ${platform.id} ` +
                            `appointment(s) from ${disposition.result.pages} page(s)`
                    );
                    finish({ status: "read", payload }, true);
                    return;
                }

                case "failed":
                    finish(
                        {
                            status: "gaveUp",
                            outcome: {
                                status: "notImported",
                                reason: disposition.reason,
                                found: disposition.found,
                                diagnostics: disposition.diagnostics,
                            },
                        },
                        true
                    );
                    return;
            }
        };

        const start = async () => {
            listeners.push(
                await browser().addListener(
                    "messageFromWebview",
                    (event) => {
                        const message = parseScrapeMessage(event?.detail);
                        if (!message) {
                            // Not ours. The page's own scripts share this bridge.
                            return;
                        }

                        // The adapters recognise a schedule by its markup, not
                        // by its host — that is the extension manifest's job,
                        // and there is no manifest here. So the host check is
                        // ours: a page on some other site that happens to carry
                        // similar markup must not be imported as this
                        // platform's appointments.
                        if (!isPlatformUrl(message.url, platform)) {
                            console.warn(
                                `REFSIX: ignoring a scrape from outside ${platform.id}`,
                                message.url
                            );
                            return;
                        }

                        act(scrapeDisposition(message), message.url);
                    }
                )
            );

            listeners.push(
                await browser().addListener("browserPageLoaded", () => {
                    // Injected per page load rather than as a preShowScript: by
                    // the time this fires the native side has installed the
                    // `window.mobileApp` bridge, and the schedule is reached by
                    // navigating (login, then the appointments list), so there
                    // is no single page to inject into up front.
                    browser().executeScript({ code }).catch((err) =>
                        console.warn(
                            "Could not inject the assignment scraper",
                            err
                        )
                    );
                })
            );

            listeners.push(
                await browser().addListener("closeEvent", () => {
                    // Already closing itself, so do not ask it to close again.
                    finish(
                        { status: "gaveUp", outcome: { status: "cancelled" } },
                        false
                    );
                })
            );

            timer = setTimeout(
                () =>
                    finish(
                        { status: "gaveUp", outcome: { status: "timedOut" } },
                        true
                    ),
                OVERALL_TIMEOUT_MS
            );

            await browser().openWebView({
                url: platform.scheduleUrl,
                title: platform.displayName,
                // The user has to be able to get out, and to retry a page that
                // failed to load on a bad connection.
                toolbarType: ToolBarType.ACTIVITY,
                showReloadButton: true,
                // A universal link would hand the sign-in off to another app,
                // stranding the session outside the WebView that needs it.
                preventDeeplink: true,
            });
        };

        start().catch((err) => {
            console.error("Could not open the assignment platform", err);
            finish(
                {
                    status: "gaveUp",
                    outcome: { status: "importFailed" },
                },
                true
            );
        });
    });
}

/**
 * Posts the scraped fixtures to refauth — the same endpoint and payload the
 * browser extension uses, so everything about turning a scraped fixture into a
 * REFSIX match stays in one place, server-side, where it can be fixed with a
 * deploy.
 */
async function sendToRefsix(
    payload: ScrapePayload
): Promise<InAppImportOutcome> {
    try {
        const response = await request.post<ImportResponse>(
            IMPORT_PATH,
            payload
        );

        return {
            status: "imported",
            platform: payload.platform,
            ...summariseImportResponse(response.data),
        };
    } catch (err) {
        const httpStatus = axios.isAxiosError(err)
            ? err.response?.status
            : undefined;
        console.error("Could not import scraped appointments", httpStatus, err);
        return {
            status: "importFailed",
            sessionExpired: httpStatus === 401,
        };
    }
}
